import { LoadError } from '@/components/LoadError';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, type Task } from '@horizon/shared';
import { EmptyState } from '@/components/EmptyState';
import { ProjectQuickAdd as QuickAdd } from '@/components/ProjectQuickAdd';
import { TaskRow } from '@/components/TaskRow';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/toast-context';
import { useScheduleTask, useMe, useQuickAdd, useSetTaskStatus, useTasks } from '@/lib/queries';

/**
 * Инбокс — задачи без даты и без проекта. Цель экрана: довести до пустого
 * без чувства вины, поэтому единственное действие — «Запланировать».
 */
export default function InboxPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const me = useMe();
  const tasks = useTasks();
  const quickAdd = useQuickAdd();
  const setStatus = useSetTaskStatus();
  const plan = useScheduleTask();
  const quickAddRef = useRef<HTMLInputElement>(null);

  const today = me.data?.today ?? '';
  const inbox = useMemo(
    () =>
      (tasks.data ?? []).filter(
        (task) => task.status === 'todo' && !task.scheduledFor && !task.projectId,
      ),
    [tasks.data],
  );

  const schedule = (task: Task, date: string, message: string) => {
    if (!date || plan.isPending) return;
    const onError = () => toast.show({ message: t('week.saveFailed') });
    plan.mutate(
      { id: task.id, scheduledFor: date },
      {
        onError,
        onSuccess: () =>
          toast.show({
            message,
            actionLabel: t('common.undo'),
            onAction: () =>
              plan.mutate({ id: task.id, scheduledFor: task.scheduledFor }, { onError }),
          }),
      },
    );
  };

  return (
    <section className="flex flex-col gap-6">
      <h1 className="font-serif text-2xl font-semibold">{t('inbox.title')}</h1>

      <QuickAdd
        ref={quickAddRef}
        today={today}
        pending={quickAdd.isPending}
        onSubmit={(input, parse, projectId) =>
          quickAdd.mutateAsync({ input, parse, projectId: projectId ?? null })
        }
      />

      {tasks.isError || me.isError ? (
        <LoadError retry={() => Promise.all([tasks.refetch(), me.refetch()])} />
      ) : tasks.isPending || me.isPending ? (
        <p role="status">{t('common.loading')}</p>
      ) : inbox.length === 0 ? (
        <EmptyState titleKey="inbox.empty.title" hintKey="inbox.empty.hint" />
      ) : (
        <ul className="flex flex-col gap-2">
          {inbox.map((task) => (
            <li key={task.id} className="flex flex-col gap-2">
              <ul className="contents">
                <TaskRow
                  task={task}
                  today={today}
                  showDefer={false}
                  onToggle={() => setStatus.mutate({ id: task.id, status: 'done' })}
                  onDefer={() => schedule(task, today, t('inbox.scheduledToday'))}
                />
              </ul>
              <div className="flex flex-wrap gap-2 pl-3">
                <Button
                  size="sm"
                  disabled={!today || plan.isPending}
                  onClick={() => schedule(task, today, t('inbox.scheduledToday'))}
                >
                  {t('inbox.scheduleToday')}
                </Button>
                <Button
                  size="sm"
                  disabled={!today || plan.isPending}
                  onClick={() => schedule(task, addDays(today, 1), t('inbox.scheduledTomorrow'))}
                >
                  {t('inbox.scheduleTomorrow')}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
